import { useState } from 'react';
import { Outlet, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { Home, Users, MessageSquare, Bell, User, AlertTriangle } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { PwaPushEnforcer } from '../components/security/PwaPushEnforcer';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { useAppVersionTelemetry } from '../features/telemetry/useAppVersionTelemetry';
import { AuditTelemetryModal } from '../features/telemetry/AuditTelemetryModal';
import { useSessionRecorder } from '../features/telemetry/session-recorder';
import './mobile.css';

const TABS = [
  { path: '/mobile', icon: Home, label: 'Início' },
  { path: '/mobile/filhos', icon: Users, label: 'Filhos' },
  { path: '/mobile/conversas', icon: MessageSquare, label: 'Mensagens' },
  { path: '/mobile/comunicados', icon: Bell, label: 'Avisos' },
  { path: '/mobile/perfil', icon: User, label: 'Perfil' },
];

export function MobileLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const perfil = useAuthStore(s => s.perfil);
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);
  const isOnline = useOnlineStatus();
  const telemetry = useAppVersionTelemetry();
  const [telemetryModalOpen, setTelemetryModalOpen] = useState(false);

  // Ativa gravação de telemetria, captura de rotas e monitoramento de erros no mobile
  useSessionRecorder();

  // ── Bonding em andamento → redirecionar para continuar ─────────────
  // O PWA abre após instalação. O login Firebase vai acontecer DENTRO
  // do PWA (não no browser). SecurityBondingPage detecta standalone
  // e pula a fase de install, indo direto pro login.
  const pendingBonding = sessionStorage.getItem('bonding_session_id') || localStorage.getItem('bonding_session_id');
  if (pendingBonding) {
    return <Navigate to={`/security/bonding?s=${pendingBonding}`} replace />;
  }

  // ── Redirecionar por perfil ────────────────────────────────────────
  // O PWA abre em /mobile (start_url do manifest.json), mas diretor/admin
  // não deve ver o dashboard de responsável — vai direto pro admin.
  if (perfil === 'ADMIN') {
    return <Navigate to="/master/dashboard" replace />;
  }
  if (perfil === 'ESCOLA') {
    return <Navigate to="/admin/dashboard" replace />;
  }
  if (perfil === 'PROFESSOR') {
    return <Navigate to="/professor/dashboard" replace />;
  }

  // ── Sem bonding e sem autenticação → tela de login ─────────────────
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // ── Bloqueio PWA Mandatório para Responsáveis no celular ──────────
  // No iframe do /simulador não redirecionar para /install — o PwaPushEnforcer cuida do push.
  const isCockpitEmbed =
    sessionStorage.getItem('ac_cockpit_embed') === '1' ||
    sessionStorage.getItem('ac_impersonate') === 'true';
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth <= 768;
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true;
  const skippedPwaInstall = localStorage.getItem('ac_skip_pwa_install') === 'true';

  if (perfil === 'RESPONSAVEL' && isMobile && !isStandalone && !skippedPwaInstall && !isCockpitEmbed) {
    const redirect = `${location.pathname}${location.search}`;
    return <Navigate to={`/install?redirect=${encodeURIComponent(redirect)}`} replace />;
  }

  return (
    <div className="mobile-shell">
      <PwaPushEnforcer />
      {/* Header */}
      <header className="mobile-header">
        <div className="mobile-header-brand">
          <div className="mobile-header-logo">AC</div>
          <span className="mobile-header-title">Aluno Conectado</span>
        </div>
        {/* Chip de Versão e Telemetria no Topo do Header Mobile */}
        <button
          type="button"
          onClick={() => setTelemetryModalOpen(true)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '4px 8px',
            borderRadius: 8,
            background: 'rgba(255, 255, 255, 0.08)',
            border: '1px solid rgba(255, 255, 255, 0.16)',
            color: '#a5b4fc',
            fontSize: '0.68rem',
            fontWeight: 700,
            cursor: 'pointer',
            fontFamily: 'monospace',
            transition: 'all .15s ease',
          }}
          title="Ver informações de versão e auditoria de homologação"
        >
          <span style={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: telemetry.isStale ? '#ef4444' : '#10b981',
            boxShadow: telemetry.isStale ? '0 0 6px #ef4444' : '0 0 6px #10b981',
          }} />
          <span>{telemetry.localManifest.version}</span>
        </button>
      </header>

      {/* Modal de Auditoria e Limpeza */}
      <AuditTelemetryModal
        open={telemetryModalOpen}
        onClose={() => setTelemetryModalOpen(false)}
        telemetry={telemetry}
      />

      {/* Offline Alert Banner */}
      {!isOnline && (
        <div style={{
          background: '#d97706',
          color: 'white',
          padding: '8px 14px',
          fontSize: '0.78rem',
          fontWeight: 700,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 6,
          boxShadow: '0 2px 8px rgba(217,119,6,0.2)',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          position: 'sticky',
          top: 0,
          zIndex: 99,
        }}>
          <AlertTriangle size={14} />
          Sem conexão com a internet. Exibindo dados salvos.
        </div>
      )}

      {/* Content */}
      <main className="mobile-content">
        <Outlet />
      </main>

      {/* Bottom Tab Bar */}
      <nav className="mobile-tab-bar">
        {TABS.map((tab) => {
          const isActive = location.pathname === tab.path;
          return (
            <button
              key={tab.path}
              className={`mobile-tab ${isActive ? 'mobile-tab-active' : ''}`}
              onClick={() => navigate(tab.path)}
            >
              <tab.icon size={20} />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </nav>
    </div>
  );
}
